import { Box, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { LenderName } from '@nemuru-eng/js-api-client';

import { LoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/ConditionsGroup/LoanConditionsGroup';
import { LoanConditions } from '@/components/modules/Application/components/PaymentMethod/_shared/types/LoanConditions';
import { ConsumerLoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/ConsumerLoan/entities/ConditionsGroup/ConsumerLoanConditionsGroup';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { TableColumn } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

interface ConditionsGroupCardDetailProps<U extends LoanConditionsGroup> {
  conditionsGroup: U;
  showMerchantCost: boolean;
}

export const ConditionsGroupCardDetail = <U extends LoanConditionsGroup>({
  conditionsGroup,
  showMerchantCost,
}: ConditionsGroupCardDetailProps<U>) => {
  const { sm } = useScreen();
  const { palette, typography } = useCustomTheme();
  const { getTextIdTranslation } = useTranslation();

  const useStyles = makeStyles({
    root: {
      width: 'max-content',
      '& .MuiTableCell-root': {
        fontSize: typography.h6.fontSize,
        lineHeight: typography.h6.lineHeight,
        paddingInline: !sm ? 4 : 6,
        paddingBlock: 4,
        borderBottom: 0,
        minWidth: 'max-width',
        '&:first-child': {
          paddingLeft: 0,
        },
      },
      '& .MuiTableRow-head': {
        verticalAlign: 'bottom',
      },
      '& .MuiTableCell-head': { fontWeight: '700' },
      '& .MuiTableRow-head .MuiTableCell-root': {
        borderBottom: `1px solid ${palette.grey[300]}`,
      },
    },
  });

  const classes = useStyles();

  const columns = [
    { name: '', label: '', align: 'left', colWidth: '20px' },
    { name: 'quota', label: 'QUOTA', align: 'right', colWidth: '60px' },
    { name: 'tin', label: 'TIN', align: 'right', colWidth: '60px' },
    { name: 'tae', label: 'TAE', align: 'right', colWidth: '60px' },
    { name: 'setup_fee', label: 'SETUP_FEE_SHORT', align: 'right', colWidth: '60px' },
    ...(showMerchantCost
      ? [{ name: 'merchant_cost', label: 'MERCHANT_COST_SHORT', align: 'right', colWidth: '60px' }]
      : []),
  ] as TableColumn[];

  if (conditionsGroup instanceof ConsumerLoanConditionsGroup) {
    if (conditionsGroup.hasGracePeriod) {
      columns.push({ name: 'grace_period', label: 'GRACE_PERIOD', align: 'right', colWidth: '60px' });
    }
  }

  return (
    <Box marginTop={1} marginBottom={0}>
      <CustomPaper style={{ padding: '3px 10px' }}>
        <TableContainer>
          <Table>
            <TableHead className={classes.root}>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.label} align={column.align} width={column.colWidth ? column.colWidth : 'auto'}>
                    {getTextIdTranslation(column.label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {conditionsGroup.conditions.map((condition, index) => (
                <ConditionsRow
                  key={`${condition.pricingId}-${index}`}
                  condition={condition}
                  classes={classes}
                  columns={columns}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CustomPaper>
    </Box>
  );
};

interface ConditionsRowProps {
  condition: LoanConditions;
  classes;
  columns: TableColumn[];
}

export const ConditionsRow = ({ condition, classes, columns }: ConditionsRowProps) => {
  const { getTextIdTranslation } = useTranslation();

  const rowData = [
    condition.providerName,
    condition.instalmentTotalAmount.string,
    condition.annualInterest?.string || '0,00 €',
    condition.apr.string,
    condition.setupFee?.value
      ? `${condition.setupFee.string} ${getTextIdTranslation(`SETUP_FEE_TYPE_SHORT_${condition.setupFeeType}`)}`
      : '0,00 %',
    condition.merchantDiscountPercentage?.value
      ? condition.merchantDiscountPercentage?.string
      : getTextIdTranslation('FREE'),
    condition.gracePeriod ? `${condition.gracePeriod} ${getTextIdTranslation('MONTHS').toLowerCase()}` : '-',
  ];

  return (
    <TableRow
      className={classes.root}
      style={{
        maxWidth: '100%',
      }}
    >
      {rowData.map((row, index) => {
        const column = columns[index];

        // if no grace period is set skip
        if (!column) return;

        // first column prints LenderLogo
        if (index === 0)
          return (
            <TableCell key={column.label} align={column.align} width={column.colWidth ? column.colWidth : 'auto'}>
              <Box display={'flex'} alignItems={'center'}>
                <LenderIcon lenderName={row as LenderName} size={20} />
              </Box>
            </TableCell>
          );

        return (
          <TableCell key={column.label} align={column.align} width={column.colWidth ? column.colWidth : 'auto'}>
            <>{row}</>
          </TableCell>
        );
      })}
    </TableRow>
  );
};
